import React, { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Fuel, Plus, Camera, Pencil, Trash2, Gauge, TrendingUp, Receipt } from "lucide-react";
import { useCanDelete } from "@/context/AuthContext";

const money = (v) => "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY = {
  bowser_id: "", odometer_km: 0, litres_filled: 0, rate_per_litre: 0, total_cost: 0,
  fuel_station: "", payment_mode: "cash", odo_photo_url: "", receipt_photo_url: "", remarks: "",
};

/** Downscale + JPEG-compress a File to a data-URL. */
async function fileToDataURL(file, maxKB = 900) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const scale = Math.min(1, 1200 / Math.max(img.width, img.height));
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        let q = 0.85;
        let out = canvas.toDataURL("image/jpeg", q);
        while (out.length / 1024 > maxKB && q > 0.35) {
          q -= 0.1;
          out = canvas.toDataURL("image/jpeg", q);
        }
        resolve(out);
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/* ─────────────────────────────────────────────────── Page shell ─── */

export default function BowserFuel() {
  const canDelete = useCanDelete();
  return (
    <div className="space-y-4" data-testid="bowser-fuel-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Bowser Fuel — Self Consumption</h1>
        <p className="text-sm text-muted-foreground">Log every time a bowser tank is refuelled at a pump for its own running. Odometer reading + photo are mandatory.</p>
      </div>
      <Tabs defaultValue="logs">
        <TabsList>
          <TabsTrigger value="logs" data-testid="tab-fuel-logs"><Receipt size={14} className="mr-1" /> Fuel Logs</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-fuel-report"><TrendingUp size={14} className="mr-1" /> Consumption Report</TabsTrigger>
        </TabsList>
        <TabsContent value="logs" className="mt-4"><FuelLogsPanel canDelete={canDelete} /></TabsContent>
        <TabsContent value="report" className="mt-4"><ConsumptionReport /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ────────────────────────────────────────── Fuel Logs orchestrator ─── */

function FuelLogsPanel({ canDelete }) {
  const qc = useQueryClient();
  const bowsers = useQuery({ queryKey: ["fleet-bowsers-full"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) }).data?.items || [];

  const [filterBowser, setFilterBowser] = useState("");
  const logs = useQuery({
    queryKey: ["bowser-fuel", filterBowser],
    queryFn: () => api.get(`/bowser-fuel?bowser_id=${filterBowser}`).then((r) => r.data),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [viewImg, setViewImg] = useState(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["bowser-fuel"] });
  const invalidateFleet = () => qc.invalidateQueries({ queryKey: ["fleet-bowsers-full"] });

  const save = useMutation({
    mutationFn: () => (editingId ? api.patch(`/bowser-fuel/${editingId}`, form) : api.post("/bowser-fuel", form)),
    onSuccess: () => {
      toast.success(editingId ? "Log updated" : "Fuel logged");
      invalidate(); invalidateFleet();
      setDialogOpen(false); setEditingId(null); setForm(EMPTY);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/bowser-fuel/${id}`),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Delete failed"),
  });

  const openNew = () => { setForm(EMPTY); setEditingId(null); setDialogOpen(true); };
  const openEdit = (l) => {
    setForm({
      bowser_id: l.bowser_id, odometer_km: l.odometer_km, litres_filled: l.litres_filled,
      rate_per_litre: l.rate_per_litre, total_cost: l.total_cost, fuel_station: l.fuel_station,
      payment_mode: l.payment_mode, odo_photo_url: l.odo_photo_url, receipt_photo_url: l.receipt_photo_url,
      remarks: l.remarks,
    });
    setEditingId(l.id); setDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <FuelLogFilters bowsers={bowsers} value={filterBowser} onChange={setFilterBowser} />
          <FuelLogFormDialog
            open={dialogOpen}
            onOpenChange={setDialogOpen}
            editingId={editingId}
            form={form}
            setForm={setForm}
            bowsers={bowsers}
            onOpenNew={openNew}
            onSave={() => save.mutate()}
            saving={save.isPending}
            setViewImg={setViewImg}
          />
        </CardHeader>
        <CardContent className="p-0">
          <FuelLogTable
            items={logs.data?.items || []}
            canDelete={canDelete}
            onEdit={openEdit}
            onDelete={(id) => del.mutate(id)}
            onPreview={setViewImg}
          />
        </CardContent>
      </Card>
      <PhotoPreviewDialog src={viewImg} onClose={() => setViewImg(null)} />
    </>
  );
}

/* ─────────────────────────────────────────────── Filter bar ─── */

function FuelLogFilters({ bowsers, value, onChange }) {
  return (
    <div className="flex flex-1 items-center gap-2">
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="max-w-52" data-testid="filter-bowser"><SelectValue placeholder="All bowsers" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All bowsers</SelectItem>
          {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no}</SelectItem>))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ─────────────────────────────────────────── Log Table ─── */

function FuelLogTable({ items, canDelete, onEdit, onDelete, onPreview }) {
  return (
    <table className="data-table w-full">
      <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
        <tr>
          <th className="px-4 py-2">Code</th>
          <th className="px-4 py-2">Date</th>
          <th className="px-4 py-2">Bowser</th>
          <th className="px-4 py-2">Driver</th>
          <th className="px-4 py-2 text-right">Odo (km)</th>
          <th className="px-4 py-2 text-right">Litres</th>
          <th className="px-4 py-2 text-right">Rate</th>
          <th className="px-4 py-2 text-right">Total</th>
          <th className="px-4 py-2 text-right">km/L</th>
          <th className="px-4 py-2">Photo</th>
          <th className="px-4 py-2 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {items.map((l) => (
          <FuelLogRow key={l.id} log={l} canDelete={canDelete} onEdit={onEdit} onDelete={onDelete} onPreview={onPreview} />
        ))}
        {items.length === 0 && (
          <tr><td colSpan={11} className="px-4 py-8 text-center text-sm text-muted-foreground">
            <Fuel size={32} className="mx-auto mb-2 opacity-40" />
            No fuel logs yet. Add the first entry with an odometer photo.
          </td></tr>
        )}
      </tbody>
    </table>
  );
}

function FuelLogRow({ log: l, canDelete, onEdit, onDelete, onPreview }) {
  return (
    <tr className="border-b border-border/40 text-sm" data-testid={`bf-row-${l.code}`}>
      <td className="px-4 py-2 font-mono">{l.code}</td>
      <td className="px-4 py-2 text-xs">{l.date?.slice(0, 10)}</td>
      <td className="px-4 py-2 font-mono">{l.bowser_code}</td>
      <td className="px-4 py-2 text-xs">{l.driver_name || "—"}</td>
      <td className="px-4 py-2 text-right font-mono text-xs">{l.odometer_km?.toLocaleString()}</td>
      <td className="px-4 py-2 text-right font-mono">{l.litres_filled}</td>
      <td className="px-4 py-2 text-right font-mono">{l.rate_per_litre?.toFixed(2)}</td>
      <td className="px-4 py-2 text-right font-mono font-semibold">{money(l.total_cost)}</td>
      <td className="px-4 py-2 text-right font-mono">{l.mileage_kmpl > 0 ? l.mileage_kmpl.toFixed(2) : "—"}</td>
      <td className="px-4 py-2">
        {l.odo_photo_url && <img src={l.odo_photo_url} alt="odo" className="h-8 w-8 cursor-pointer rounded border border-border object-cover" onClick={() => onPreview(l.odo_photo_url)} data-testid={`bf-photo-${l.code}`} />}
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => onEdit(l)} data-testid={`bf-edit-${l.code}`}><Pencil size={12} /></Button>
          {canDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild><Button size="sm" variant="ghost" data-testid={`bf-del-${l.code}`}><Trash2 size={12} /></Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete {l.code}?</AlertDialogTitle><AlertDialogDescription>Cannot be undone. Only admin, accounts, or manager roles can delete fuel logs.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(l.id)} data-testid={`bf-del-confirm-${l.code}`}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────── Log Form Dialog ─── */

function FuelLogFormDialog({ open, onOpenChange, editingId, form, setForm, bowsers, onOpenNew, onSave, saving, setViewImg }) {
  const odoRef = useRef(null);
  const rcptRef = useRef(null);

  const upd = (k) => (e) => {
    const val = e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e;
    const next = { ...form, [k]: val };
    if (k === "litres_filled" || k === "rate_per_litre") {
      const litres = k === "litres_filled" ? val : form.litres_filled;
      const rate = k === "rate_per_litre" ? val : form.rate_per_litre;
      next.total_cost = Number((litres * rate || 0).toFixed(2));
    }
    setForm(next);
  };

  const handleFile = async (kind, file) => {
    if (!file) return;
    try {
      const dataUrl = await fileToDataURL(file);
      setForm((f) => ({ ...f, [kind]: dataUrl }));
    } catch {
      toast.error("Could not read image");
    }
  };

  const canSave = form.bowser_id && form.odometer_km > 0 && form.litres_filled > 0 && form.odo_photo_url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild><Button onClick={onOpenNew} data-testid="btn-new-fuel"><Plus size={14} className="mr-1" /> Log fuel</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editingId ? "Edit fuel log" : "Log bowser self-fuel"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Bowser <span className="text-status-cancelled">*</span></Label>
            <Select value={form.bowser_id} onValueChange={upd("bowser_id")}>
              <SelectTrigger data-testid="bf-bowser"><SelectValue placeholder="Select bowser" /></SelectTrigger>
              <SelectContent>
                {bowsers.map((b) => (<SelectItem key={b.id} value={b.id}>{b.code} — {b.registration_no} (odo: {b.odometer_km?.toLocaleString() || 0} km)</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Odometer reading (km) <span className="text-status-cancelled">*</span></Label><Input type="number" value={form.odometer_km || ""} onChange={upd("odometer_km")} placeholder="e.g. 65432" data-testid="bf-odo" /></div>
          <div><Label>Litres filled <span className="text-status-cancelled">*</span></Label><Input type="number" step="0.01" value={form.litres_filled || ""} onChange={upd("litres_filled")} data-testid="bf-litres" /></div>
          <div><Label>Rate per litre (₹)</Label><Input type="number" step="0.01" value={form.rate_per_litre || ""} onChange={upd("rate_per_litre")} /></div>
          <div><Label>Total cost (₹)</Label><Input type="number" step="0.01" value={form.total_cost || ""} onChange={upd("total_cost")} data-testid="bf-total" /></div>
          <div><Label>Fuel station</Label><Input value={form.fuel_station} onChange={upd("fuel_station")} placeholder="IOCL Bhosari" /></div>
          <div>
            <Label>Payment mode</Label>
            <Select value={form.payment_mode} onValueChange={upd("payment_mode")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="company_card">Company Card</SelectItem>
                <SelectItem value="fastag">FASTag Fuel</SelectItem>
                <SelectItem value="credit">Credit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <PhotoField
            required
            title="Odometer photo"
            testId="bf-odo-file"
            inputRef={odoRef}
            src={form.odo_photo_url}
            onFile={(f) => handleFile("odo_photo_url", f)}
            onPreview={setViewImg}
            capture
          />
          <PhotoField
            title="Bill / receipt photo (optional)"
            inputRef={rcptRef}
            src={form.receipt_photo_url}
            onFile={(f) => handleFile("receipt_photo_url", f)}
            onPreview={setViewImg}
          />

          <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={upd("remarks")} /></div>
        </div>
        <DialogFooter>
          <Button onClick={onSave} disabled={!canSave || saving} data-testid="btn-save-fuel">
            {saving ? "Saving…" : editingId ? "Update log" : "Log fuel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoField({ title, required, testId, inputRef, src, onFile, onPreview, capture }) {
  const containerClass = required
    ? "col-span-2 rounded-md border-2 border-dashed border-primary/40 bg-primary/5 p-3"
    : "col-span-2 rounded-md border border-border/60 p-3";
  const labelClass = required
    ? "mb-2 flex items-center gap-2 text-sm font-bold text-primary"
    : "mb-2 flex items-center gap-2 text-sm font-bold text-muted-foreground";
  const Icon = required ? Camera : Receipt;
  return (
    <div className={containerClass}>
      <div className={labelClass}>
        <Icon size={16} /> {title} {required && <span className="text-status-cancelled">*</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture={capture ? "environment" : undefined}
        className="text-xs"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={testId}
      />
      {src && (
        <img
          src={src}
          alt={title}
          className={`mt-2 rounded border border-border cursor-pointer ${required ? "max-h-40" : "max-h-32"}`}
          onClick={() => onPreview(src)}
        />
      )}
    </div>
  );
}

function PhotoPreviewDialog({ src, onClose }) {
  return (
    <Dialog open={!!src} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Photo preview</DialogTitle></DialogHeader>
        {src && <img src={src} alt="preview" className="max-h-[70vh] w-full rounded object-contain" />}
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────── Consumption Report ─── */

function ConsumptionReport() {
  const { data } = useQuery({ queryKey: ["bowser-fuel-report"], queryFn: () => api.get("/bowser-fuel/report").then((r) => r.data) });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi label="Entries" value={data?.entries || 0} />
        <Kpi label="Total litres" value={(data?.total_litres || 0).toLocaleString("en-IN")} />
        <Kpi label="Total cost" value={money(data?.total_cost)} />
        <Kpi label="Km travelled" value={(data?.total_km || 0).toLocaleString("en-IN")} />
        <Kpi label="Fleet mileage" value={(data?.overall_mileage || 0).toFixed(2) + " km/L"} icon={Gauge} />
      </div>

      <ReportBowserTable rows={data?.by_bowser || []} />
      <ReportDriverTable rows={data?.by_driver || []} />
    </div>
  );
}

function ReportBowserTable({ rows }) {
  return (
    <Card>
      <CardHeader><CardTitle>By bowser</CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Registration</th><th className="px-4 py-2 text-right">Entries</th><th className="px-4 py-2 text-right">Litres</th><th className="px-4 py-2 text-right">Cost</th><th className="px-4 py-2 text-right">Km</th><th className="px-4 py-2 text-right">Mileage</th><th className="px-4 py-2 text-right">Last odo</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.bowser_id} className="border-b border-border/40 text-sm" data-testid={`report-bowser-${r.bowser_code}`}>
                <td className="px-4 py-2 font-mono">{r.bowser_code}</td>
                <td className="px-4 py-2 font-mono text-xs">{r.bowser_reg}</td>
                <td className="px-4 py-2 text-right font-mono">{r.entries}</td>
                <td className="px-4 py-2 text-right font-mono">{r.litres.toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono">{money(r.cost)}</td>
                <td className="px-4 py-2 text-right font-mono">{r.km_travelled?.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono font-bold text-primary">{r.avg_mileage.toFixed(2)} km/L</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{r.last_odometer?.toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">No data yet.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function ReportDriverTable({ rows }) {
  return (
    <Card>
      <CardHeader><CardTitle>By driver</CardTitle></CardHeader>
      <CardContent className="p-0">
        <table className="data-table w-full">
          <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
            <tr><th className="px-4 py-2">Driver</th><th className="px-4 py-2 text-right">Entries</th><th className="px-4 py-2 text-right">Litres</th><th className="px-4 py-2 text-right">Cost</th><th className="px-4 py-2 text-right">Km</th><th className="px-4 py-2 text-right">Mileage</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.driver_id} className="border-b border-border/40 text-sm">
                <td className="px-4 py-2">{r.driver_name}</td>
                <td className="px-4 py-2 text-right font-mono">{r.entries}</td>
                <td className="px-4 py-2 text-right font-mono">{r.litres.toFixed(2)}</td>
                <td className="px-4 py-2 text-right font-mono">{money(r.cost)}</td>
                <td className="px-4 py-2 text-right font-mono">{r.km_travelled?.toLocaleString()}</td>
                <td className="px-4 py-2 text-right font-mono font-bold text-primary">{r.avg_mileage.toFixed(2)} km/L</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No data yet.</td></tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

const Kpi = ({ label, value, icon: Icon }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      {Icon && <Icon size={14} />}
    </div>
    <div className="mt-2 font-display text-xl font-black">{value}</div>
  </div>
);
